import React from 'react';
import { TourStep } from './tours';

/** Small line icons for the step cards, picked from what the step points at. */
type IconName = 'spark' | 'search' | 'bell' | 'user' | 'trophy' | 'upload' | 'scale' | 'sliders' | 'map' | 'doc' | 'play'
  | 'grid' | 'clock' | 'key' | 'flag' | 'alert';

const PATHS: Record<IconName, React.ReactNode> = {
  spark: <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></>,
  bell: <><path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></>,
  trophy: <><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8" /></>,
  upload: <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 16v3h14v-3" /></>,
  scale: <><path d="M12 4v16M7 20h10M5 7h14" /><path d="M5 7l-2.5 6a2.5 2.5 0 0 0 5 0zM19 7l-2.5 6a2.5 2.5 0 0 0 5 0z" /></>,
  sliders: <><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></>,
  map: <><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></>,
  doc: <><path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5M10 13h6M10 17h6" /></>,
  play: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3z" /></>,
  grid: <><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  key: <><circle cx="8" cy="15" r="4" /><path d="M11 12l8-8M16 7l2 2M14 9l2 2" /></>,
  flag: <><path d="M5 21V4M5 4h11l-2 4 2 4H5" /></>,
  alert: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5M12 16h.01" /></>,
};

/** Checked in order: the first pattern that matches the step's target wins. */
const RULES: [RegExp, IconName][] = [
  [/error/, 'alert'],
  [/deadline|countdown/, 'clock'],
  [/login|register/, 'key'],
  [/hero|competition-header|pills|meta|competition-action/, 'flag'],
  [/competitions|grid|work-list/, 'grid'],
  [/search|list-controls|filter|picker|select|tracks/, 'search'],
  [/notif|alert/, 'bell'],
  [/account|settings|profile|user|security|appearance/, 'user'],
  [/leaderboard|stats|rank|showcase/, 'trophy'],
  [/submit|submission|upload|run|terminal|practice|logs|cta/, 'upload'],
  [/eval|judg|vote|score|rubric|feedback|pair|allowance/, 'scale'],
  [/manage|admin|details|people|danger|logo|actions|present-names/, 'sliders'],
  [/nav|tabs|menu|breadcrumbs|tour-button|footer/, 'map'],
  [/legal|markdown|instructions|overview|content|message/, 'doc'],
  [/video|present|stage|fullscreen/, 'play'],
];

export function iconFor(step: TourStep): IconName {
  if (!step.target) return 'spark';
  return RULES.find(([pattern]) => pattern.test(step.target as string))?.[1] || 'spark';
}

export const TourIcon: React.FC<{ step: TourStep }> = ({ step }) => (
  <span className={`tour-card__icon tour-card__icon--${iconFor(step)}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" focusable="false">
      {PATHS[iconFor(step)]}
    </svg>
  </span>
);
